import React, {useCallback, useState, useEffect} from "react";
import {QueryClient, QueryClientProvider} from "react-query";
import {useLocation, useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {ACard} from "@atiautomacao/ati-ui-library";
import {HeaderMenu} from "../HeaderMenu";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {TicketCategory} from "../../../Shared/Types/TicketCategory";
import {TicketSubCategory} from "../../../Shared/Types/TicketSubCategory";
import {
    CreateTicketSubCategory,
    UpdateTicketSubCategory,
    FindByIdTicketSubCategory
} from "./TicketSubCategoryReactQuery";
import {FindAllTicketCategory} from "../TicketCategory/TicketCategoryReactQuery";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import { useAppSelector } from "../../../Config/Hooks";
import { AUTHORITIES } from "../../../Config/Constants";

interface TicketSubCategoryFormProps {
    id?: string,
    handleBack: Function
}

function TicketSubCategoryForm(props: TicketSubCategoryFormProps) {
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSave = hasPermission(account?.authoritySet, [ AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_TICKET_SUBCATEGORY,AUTHORITIES.UPDATE_TICKET_SUBCATEGORY]);
    const isAuthorizedToTicketCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_CATEGORY, AUTHORITIES.VIEW_TICKET_CATEGORY
    ]); 

    const {data: findById, isSuccess} = FindByIdTicketSubCategory(Number(props.id));
    const {data: categories} = FindAllTicketCategory();
    const [formData, setFormData] = useState<TicketSubCategory>({ name: "" });
    const createTicketSubCategory = CreateTicketSubCategory();
    const updateTicketSubCategory = UpdateTicketSubCategory();

    const handleSave = () => {
        const opts = {
            onSuccess: () => props.handleBack()
        };
        if (formData.id) {
            updateTicketSubCategory.mutate(formData, opts);
        } else {
            createTicketSubCategory.mutate(formData, opts);
        }
    };

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    useEffect(() => {
        if (isSuccess && findById.data) {
            setFormData(findById.data);
        }
    }, [findById]);

    return (
        <>
            <HeaderMenu isOnSave={true} handleSave={handleSave} systemRoutes={SystemRoutes.SETTINGS_TICKET_SUBCATEGORY} disableSave={!isAuthorizedToSave && !isAuthorizedToTicketCategorySearch}/>

            <Box style={{paddingTop: 10}}>
                <ACard styleProps={{ headerStyle: {padding: 0} }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item md={4} xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="categoryLabel">Categoria</InputLabel>
                                    <Select
                                        labelId="categoryLabel"
                                        name="categoryId"
                                        value={formData.categoryId}
                                        variant={"standard"}
                                        label="Categoria"
                                        onChange={handleChange}
                                    >
                                        {categories?.data.map((ticketCategory: TicketCategory) => (
                                          <MenuItem key={ticketCategory.id} value={ticketCategory.id}>
                                            {ticketCategory.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={8} xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        name="name"
                                        value={formData.name}
                                        variant="standard"
                                        label="Nome"
                                        onChange={handleChange}
                                     />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </ACard>
            </Box>
        </>
    );
}

export default function TicketSubCategoryFormPage() {
    const queryClient = new QueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const id = location.state?.id as string;

    return (
        <QueryClientProvider client={queryClient}>
            <TicketSubCategoryForm id={id} handleBack={() => { navigate(SystemRoutes.SETTINGS_TICKET_SUBCATEGORY); }} />
        </QueryClientProvider>
    )
}